import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../config';
import { getUserData } from './user';
import { handleAuthStateChange } from './handlers/authState';
import type { User } from 'firebase/auth';
import type { UserData } from './types';
import { toast } from 'sonner';

export interface AuthStateChange {
  user: User | null;
  userData: UserData | null;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

async function getUserDataWithRetry(userId: string, retries = 0): Promise<UserData | null> {
  try {
    const userData = await getUserData(userId);
    if (userData) {
      return userData;
    }
    
    if (retries < MAX_RETRIES) {
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      return getUserDataWithRetry(userId, retries + 1);
    }
    
    return null;
  } catch (error) {
    if (retries < MAX_RETRIES) {
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      return getUserDataWithRetry(userId, retries + 1);
    }
    throw error;
  }
}

export function subscribeToAuthChanges(
  onStateChange: (state: AuthStateChange) => void,
  onError?: (error: Error) => void
) {
  let isHandlingError = false;

  return onAuthStateChanged(
    auth,
    async (user) => {
      try {
        if (user) {
          try {
            const userData = await getUserDataWithRetry(user.uid);
            if (userData) {
              onStateChange({ user, userData });
            } else {
              // If no user data exists after retries, sign out the user
              await signOut(auth);
              onStateChange({ user: null, userData: null });
              if (onError && !isHandlingError) {
                isHandlingError = true;
                onError(new Error('User data not found. Please try signing in again.'));
                setTimeout(() => { isHandlingError = false; }, 1000);
              }
            }
          } catch (error) {
            console.error('Error fetching user data:', error);
            onStateChange({ user: null, userData: null });
            if (onError && !isHandlingError) {
              isHandlingError = true;
              onError(error instanceof Error ? error : new Error('Failed to fetch user data. Please try again.'));
              setTimeout(() => { isHandlingError = false; }, 1000);
            }
          }
        } else {
          onStateChange({ user: null, userData: null });
        }
      } catch (error) {
        console.error('Auth state change error:', error);
        onStateChange({ user: null, userData: null });
        if (onError && !isHandlingError) {
          isHandlingError = true;
          onError(error instanceof Error ? error : new Error('Auth state change failed. Please try again.'));
          setTimeout(() => { isHandlingError = false; }, 1000);
        }
      }
    },
    (error) => {
      console.error('Auth state error:', error);
      onStateChange({ user: null, userData: null });
      if (onError && !isHandlingError) {
        isHandlingError = true;
        onError(error);
        setTimeout(() => { isHandlingError = false; }, 1000);
      }
    }
  );
}

export async function logoutUser(): Promise<void> {
  try {
    await signOut(auth);
  } catch (error) {
    handleAuthStateChange(error);
    throw new Error('Failed to logout');
  }
}